import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Input,
  Select,
  Tag,
  Avatar,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Spin,
  Button,
  Modal,
  Form,
  App,
  Tree,
  Switch,
} from "antd";
import {
  UserOutlined,
  SearchOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
  DownOutlined,
  UserSwitchOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title } = Typography;
const { Option } = Select;

const Users = () => {
  const { message, modal } = App.useApp();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [filterTenant, setFilterTenant] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [stats, setStats] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [createLoading, setCreateLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [viewMode, setViewMode] = useState("hierarchical"); // 'hierarchical' or 'table'
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Auto-expand organization nodes when data loads
  useEffect(() => {
    if (
      users.length > 0 &&
      organizations.length > 0 &&
      viewMode === "hierarchical"
    ) {
      const treeData = convertUsersToTreeData(users);
      const orgKeys = treeData.map((org) => org.key);
      setExpandedKeys(orgKeys);
    }
  }, [users, organizations, viewMode]);
  const [selectedUser, setSelectedUser] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  console.log("API_BASE_URL", API_BASE_URL);
  useEffect(() => {
    fetchUsers();
    fetchTenants();
    fetchOrganizations();
  }, [
    searchText,
    filterTenant,
    filterRole,
    filterStatus,
    pagination.current,
    pagination.pageSize, // Add pagination.pageSize to dependencies
    viewMode,
  ]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("super_admin_token");

      const params = new URLSearchParams({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        tenant: filterTenant,
        role: filterRole,
        status: filterStatus,
        hierarchical: viewMode === "hierarchical" ? "true" : "false",
      });

      const response = await axios.get(`${API_BASE_URL}/users?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        console.log("API returned users:", response.data.data.users.length);
        console.log("Setting users state with:", response.data.data.users);
        setUsers(response.data.data.users);
        setStats(response.data.data.stats);
        setPagination((prev) => ({
          ...prev,
          total: response.data.data.pagination.totalRecords,
        }));
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await axios.get(`${API_BASE_URL}/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setTenants(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching tenants:", error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await axios.get(`${API_BASE_URL}/organizations`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setOrganizations(response.data.data.organizations);
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const handleCreateUser = async (values) => {
    try {
      setCreateLoading(true);
      const token = localStorage.getItem("super_admin_token");

      const response = await axios.post(`${API_BASE_URL}/users`, values, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        message.success("User created successfully");
        setCreateModalVisible(false);
        createForm.resetFields();
        fetchUsers();
      }
    } catch (error) {
      console.error("Error creating user:", error);
      message.error(error.response?.data?.message || "Failed to create user");
    } finally {
      setCreateLoading(false);
    }
  };

  const showCreateModal = () => {
    setCreateModalVisible(true);
  };

  const handleCreateCancel = () => {
    setCreateModalVisible(false);
    createForm.resetFields();
  };

  const handleEdit = (record) => {
    setEditingUser(record);
    editForm.setFieldsValue({
      firstName: record.firstName,
      lastName: record.lastName,
      email: record.email,
      role: record.role,
      phone: record.phone,
      department: record.department,
      isActive: record.isActive,
      tenantId: record.tenantId, // Add this line
      emailVerified: record.emailVerified, // Add this line
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await axios.put(
        `${API_BASE_URL}/users/${editingUser._id}`,
        values,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        message.success("User updated successfully");
        setEditModalVisible(false);
        setEditingUser(null);
        editForm.resetFields();
        fetchUsers();
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update user");
    }
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setEditingUser(null);
    editForm.resetFields();
  };

  const handleToggleStatus = async (record) => {
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await axios.patch(
        `${API_BASE_URL}/users/${record._id}/status`,
        { isActive: !record.isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        message.success(response.data.message);
        fetchUsers();
      }
    } catch (error) {
      message.error("Failed to update user status");
    }
  };

  const handleDelete = (record) => {
    modal.confirm({
      title: "Delete User",
      content: `Are you sure you want to delete user "${record.firstName} ${record.lastName}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          const token = localStorage.getItem("super_admin_token");
          console.log(
            "Attempting to delete user:",
            record._id,
            "with token:",
            token
          );

          if (!token) {
            message.error(
              "Authentication token not found. Please login again."
            );
            return;
          }

          const response = await axios.delete(
            `${API_BASE_URL}/users/${record._id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          console.log("Delete response:", response.data);

          if (response.data.success) {
            message.success("User deleted successfully");
            fetchUsers();
          } else {
            message.error(response.data.message || "Failed to delete user");
          }
        } catch (error) {
          console.error("Delete user error:", error);
          if (error.response?.status === 401) {
            message.error("Authentication failed. Please login again.");
          } else {
            message.error(
              error.response?.data?.message || "Failed to delete user"
            );
          }
        }
      },
    });
  };

  const handleTableChange = (paginationInfo) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize, // Update pageSize as well
    }));
  };

  const getStatusColor = (isActive) => {
    return isActive ? "success" : "error";
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "super_admin":
        return "red";
      case "tenant_admin":
        return "orange";
      case "crm_user":
        return "blue";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "User",
      key: "user",
      render: (text, record) => {
        console.log(
          "Rendering user row:",
          record.firstName,
          record.lastName,
          "isActive:",
          record.isActive
        );
        return (
          <Space>
            <Avatar
              icon={<UserOutlined />}
              className="user-avatar"
              style={{
                backgroundColor: record.isActive ? "#52c41a" : "#ff4d4f",
              }}
            />
            <div>
              <div style={{ fontWeight: 500 }}>
                {`${record.firstName} ${record.lastName}`}
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {record.email}
              </div>
            </div>
          </Space>
        );
      },
    },
    {
      title: "Organization",
      key: "tenant",
      render: (text, record) => {
        const tenant = tenants.find((t) => t._id === record.tenantId);
        return (
          <Tag icon={<TeamOutlined />} color="blue">
            {tenant ? tenant.name : "Unknown"}
          </Tag>
        );
      },
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag color={getRoleColor(role)}>
          {role.replace("_", " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => phone || "N/A",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "status",
      render: (isActive) => {
        console.log(
          "Rendering status for user with isActive:",
          isActive,
          typeof isActive
        );
        return (
          <div className="status-badge">
            {isActive ? (
              <Tag icon={<CheckCircleOutlined />} color="success">
                Active
              </Tag>
            ) : (
              <Tag icon={<CloseCircleOutlined />} color="error">
                Inactive
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Last Login",
      dataIndex: "lastLogin",
      key: "lastLogin",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "Never"),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit User"
            style={{ backgroundColor: "#1890ff", borderColor: "#1890ff" }}
          />
          <Button
            size="small"
            icon={<PoweroffOutlined />}
            onClick={() => handleToggleStatus(record)}
            style={{
              color: record.isActive ? "#ff4d4f" : "#52c41a",
              borderColor: record.isActive ? "#ff4d4f" : "#52c41a",
            }}
            title={record.isActive ? "Deactivate" : "Activate"}
          />
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            title="Delete User"
          />
        </Space>
      ),
    },
  ];

  // Get unique values for filters
  const uniqueTenants = [...new Set(users.map((user) => user.tenant))];
  const uniqueRoles = [...new Set(users.map((user) => user.role))];

  // Calculate totals from stats
  // Use pagination.total for total users, as it comes from the API
  const totalUsers = pagination.total;
  const totalActive = stats?.activeUsers || 0; // Access directly, provide default
  const totalAdmins = stats?.adminUsers || 0; // Access directly, provide default

  // Enhanced helper function to convert hierarchical users to tree data
  // Structure: Organization → Tenant Admin → CRM Users
  const convertUsersToTreeData = (users) => {
    // Create organization lookup map
    const orgMap = organizations.reduce((map, org) => {
      map[org._id] = org.name;
      return map;
    }, {});

    // Group users by organization/tenant
    const organizationGroups = {};

    users.forEach((user) => {
      const tenantId = user.tenantId || user.tenant || "unknown";
      // Use actual organization name from orgMap, fallback to derived name
      const tenantName =
        orgMap[tenantId] ||
        user.tenantName ||
        user.organizationName ||
        `Organization ${tenantId}`;

      if (!organizationGroups[tenantId]) {
        organizationGroups[tenantId] = {
          tenantId,
          tenantName,
          admins: [],
          users: [],
        };
      }

      if (user.role === "tenant_admin" || user.role === "admin") {
        organizationGroups[tenantId].admins.push(user);
        // Add children users under this admin
        if (user.children && user.children.length > 0) {
          organizationGroups[tenantId].users.push(...user.children);
        }
      } else if (user.role === "crm_user" || user.role === "user") {
        // Only add if not already added as a child
        if (!user.isChild) {
          organizationGroups[tenantId].users.push(user);
        }
      }
    });

    // Convert to tree structure
    return Object.values(organizationGroups).map((org) => {
      const orgNode = {
        title: org.tenantName,
        key: `org-${org.tenantId}`,
        icon: <ApartmentOutlined style={{ color: "#722ed1" }} />,
        isOrganization: true,
        tenantId: org.tenantId,
        children: [],
      };

      // Add tenant admins as children of organization
      org.admins.forEach((admin) => {
        const adminNode = {
          title: `${admin.firstName} ${admin.lastName}`,
          key: `admin-${admin._id}`,
          icon: <UserSwitchOutlined style={{ color: "#f5222d" }} />,
          user: admin,
          isAdmin: true,
          children: [],
        };

        // Add CRM users under this admin
        const adminUsers = org.users.filter(
          (user) =>
            user.createdBy === admin._id ||
            user.parentId === admin._id ||
            (admin.children &&
              admin.children.some((child) => child._id === user._id))
        );

        adminUsers.forEach((crmUser) => {
          adminNode.children.push({
            title: `${crmUser.firstName} ${crmUser.lastName}`,
            key: `user-${crmUser._id}`,
            icon: <UserOutlined style={{ color: "#1890ff" }} />,
            user: crmUser,
            isCrmUser: true,
            isLeaf: true,
          });
        });

        // Add orphaned CRM users (not under any specific admin) to first admin
        if (org.admins.indexOf(admin) === 0) {
          const orphanedUsers = org.users.filter(
            (user) =>
              !org.admins.some(
                (adm) =>
                  adm.children &&
                  adm.children.some((child) => child._id === user._id)
              ) && !adminUsers.includes(user)
          );

          orphanedUsers.forEach((orphanUser) => {
            adminNode.children.push({
              title: `${orphanUser.firstName} ${orphanUser.lastName}`,
              key: `user-${orphanUser._id}`,
              icon: <UserOutlined style={{ color: "#52c41a" }} />,
              user: orphanUser,
              isCrmUser: true,
              isLeaf: true,
            });
          });
        }

        orgNode.children.push(adminNode);
      });

      // If no admins, add users directly under organization
      if (org.admins.length === 0 && org.users.length > 0) {
        org.users.forEach((user) => {
          orgNode.children.push({
            title: `${user.firstName} ${user.lastName}`,
            key: `user-${user._id}`,
            icon: <UserOutlined style={{ color: "#faad14" }} />,
            user: user,
            isCrmUser: true,
            isLeaf: true,
          });
        });
      }

      return orgNode;
    });
  };

  // Helper function to flatten hierarchical users for table view
  const flattenUsers = (users) => {
    const flattened = [];
    users.forEach((user) => {
      if (user.isParent) {
        flattened.push(user);
        if (user.children) {
          flattened.push(...user.children);
        }
      } else {
        flattened.push(user);
      }
    });
    return flattened;
  };

  // Enhanced custom tree node renderer for Organization → Admin → User hierarchy
  const renderTreeNode = (nodeData) => {
    const { user, isOrganization, isAdmin, isCrmUser } = nodeData;

    // Handle organization nodes
    if (isOrganization) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "12px 16px",
            borderRadius: "8px",
            backgroundColor: "#f8f9fa",
            border: "2px solid #e9ecef",
            fontWeight: "bold",
            fontSize: "16px",
            color: "#722ed1",
            cursor: "default",
          }}
        >
          <ApartmentOutlined style={{ fontSize: "18px", color: "#722ed1" }} />
          <span>{nodeData.title}</span>
          <Tag color="purple" style={{ marginLeft: "auto" }}>
            Organization
          </Tag>
        </div>
      );
    }

    // Handle user nodes (both admin and CRM users)
    if (user) {
      const isSelected = selectedUser && selectedUser._id === user._id;

      return (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            padding: "10px 14px",
            marginLeft: isCrmUser ? "20px" : "0px", // Indent CRM users under admins
            borderRadius: "6px",
            backgroundColor: isSelected
              ? "#e6f7ff"
              : isAdmin
              ? "#fff2e8"
              : isCrmUser
              ? "#f6ffed"
              : "transparent",
            border: isSelected
              ? "2px solid #1890ff"
              : isAdmin
              ? "1px solid #ffbb96"
              : "1px solid transparent",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: isSelected
              ? "0 2px 8px rgba(24, 144, 255, 0.2)"
              : "none",
          }}
          onClick={() => setSelectedUser(user)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Avatar
              size={isAdmin ? "default" : "small"}
              icon={<UserOutlined />}
              style={{
                backgroundColor: isAdmin
                  ? "#f5222d"
                  : isCrmUser
                  ? "#1890ff"
                  : "#52c41a",
                fontSize: isAdmin ? "16px" : "14px",
              }}
            />
            <div>
              <div
                style={{
                  fontWeight: isAdmin ? "600" : "normal",
                  fontSize: isAdmin ? "15px" : "14px",
                  color: isSelected ? "#1890ff" : "inherit",
                  marginBottom: "2px",
                }}
              >
                {user.firstName} {user.lastName}
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {user.email}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag color={isAdmin ? "red" : "blue"} size="small">
              {user.role === "tenant_admin" ? "Admin" : "User"}
            </Tag>
            <Tag color={user.isActive ? "success" : "default"} size="small">
              {user.isActive ? "Active" : "Inactive"}
            </Tag>
            <Space size="small">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(user);
                }}
              />
              <Button
                type="text"
                size="small"
                icon={<PoweroffOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleStatus(user);
                }}
              />
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(user);
                }}
              />
            </Space>
          </div>
        </div>
      );
    }

    // Default fallback
    return (
      <div style={{ padding: "8px", color: "#999" }}>Unknown node type</div>
    );
  };

  // User Details Panel Component
  const UserDetailsPanel = ({ user }) => {
    const isAdmin = user.role === "tenant_admin";

    return (
      <div style={{ padding: "16px" }}>
        {/* User Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            marginBottom: "24px",
            paddingBottom: "16px",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Avatar
            size={64}
            icon={<UserOutlined />}
            style={{
              backgroundColor: isAdmin ? "#1890ff" : "#52c41a",
              fontSize: "24px",
            }}
          />
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {user.firstName} {user.lastName}
            </Title>
            <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
              {user.email}
            </Typography.Text>
            <div style={{ marginTop: "8px" }}>
              <Tag color={isAdmin ? "blue" : "green"} size="default">
                {user.role === "tenant_admin" ? "Tenant Admin" : "CRM User"}
              </Tag>
              <Tag color={user.isActive ? "success" : "default"} size="default">
                {user.isActive ? "Active" : "Inactive"}
              </Tag>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div style={{ marginBottom: "24px" }}>
          <Title level={4}>Personal Information</Title>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div>
                <Typography.Text strong>First Name:</Typography.Text>
                <div>{user.firstName || "N/A"}</div>
              </div>
            </Col>
            <Col span={12}>
              <div>
                <Typography.Text strong>Last Name:</Typography.Text>
                <div>{user.lastName || "N/A"}</div>
              </div>
            </Col>
            <Col span={12}>
              <div>
                <Typography.Text strong>Email:</Typography.Text>
                <div>{user.email}</div>
              </div>
            </Col>
            <Col span={12}>
              <div>
                <Typography.Text strong>Phone:</Typography.Text>
                <div>{user.phone || "N/A"}</div>
              </div>
            </Col>
            <Col span={12}>
              <div>
                <Typography.Text strong>Department:</Typography.Text>
                <div>{user.department || "N/A"}</div>
              </div>
            </Col>
            <Col span={12}>
              <div>
                <Typography.Text strong>Organization:</Typography.Text>
                <div>
                  {(() => {
                    const organization = organizations.find(
                      (org) => org._id === user.tenantId
                    );
                    return organization ? organization.name : user.tenantId;
                  })()}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Account Information */}
        <div style={{ marginBottom: "24px" }}>
          <Title level={4}>Account Information</Title>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div>
                <Typography.Text strong>Role:</Typography.Text>
                <div>{user.role}</div>
              </div>
            </Col>
            <Col span={12}>
              <div>
                <Typography.Text strong>Status:</Typography.Text>
                <div>
                  <Tag color={user.isActive ? "success" : "default"}>
                    {user.isActive ? "Active" : "Inactive"}
                  </Tag>
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div>
                <Typography.Text strong>Email Verified:</Typography.Text>
                <div>
                  <Tag color={user.emailVerified ? "success" : "warning"}>
                    {user.emailVerified ? "Verified" : "Not Verified"}
                  </Tag>
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div>
                <Typography.Text strong>Created:</Typography.Text>
                <div>{new Date(user.createdAt).toLocaleDateString()}</div>
              </div>
            </Col>
            {user.lastLogin && (
              <Col span={12}>
                <div>
                  <Typography.Text strong>Last Login:</Typography.Text>
                  <div>{new Date(user.lastLogin).toLocaleString()}</div>
                </div>
              </Col>
            )}
            {user.updatedAt && (
              <Col span={12}>
                <div>
                  <Typography.Text strong>Last Updated:</Typography.Text>
                  <div>{new Date(user.updatedAt).toLocaleString()}</div>
                </div>
              </Col>
            )}
          </Row>
        </div>

        {/* Preferences (if available) */}
        {user.preferences && (
          <div style={{ marginBottom: "24px" }}>
            <Title level={4}>Preferences</Title>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div>
                  <Typography.Text strong>Language:</Typography.Text>
                  <div>{user.preferences.language || "N/A"}</div>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Typography.Text strong>Theme:</Typography.Text>
                  <div>{user.preferences.theme || "N/A"}</div>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Typography.Text strong>Timezone:</Typography.Text>
                  <div>{user.preferences.timezone || "N/A"}</div>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Typography.Text strong>Email Notifications:</Typography.Text>
                  <div>
                    <Tag
                      color={
                        user.preferences.notifications?.email
                          ? "success"
                          : "default"
                      }
                    >
                      {user.preferences.notifications?.email
                        ? "Enabled"
                        : "Disabled"}
                    </Tag>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            paddingTop: "16px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(user)}
          >
            Edit User
          </Button>
          <Button
            icon={<PoweroffOutlined />}
            onClick={() => handleToggleStatus(user)}
          >
            {user.isActive ? "Deactivate" : "Activate"}
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(user)}
          >
            Delete User
          </Button>
        </div>
      </div>
    );
  };

  console.log("Rendering Users component with users:", users.length);

  return (
    <div style={{ padding: "24px" }}>
      <Title level={2}>CRM Users Management</Title>

      {/* Filters and View Toggle */}
      <Card style={{ marginBottom: "16px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <Title level={4} style={{ margin: 0 }}>
              Users ({users.length})
            </Title>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <ApartmentOutlined />
              <Switch
                checked={viewMode === "hierarchical"}
                onChange={(checked) =>
                  setViewMode(checked ? "hierarchical" : "table")
                }
                checkedChildren="Tree"
                unCheckedChildren="Table"
              />
            </div>
          </div>
          <div>
            <Space>
              <Input
                placeholder="Search users..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 250 }}
              />
              <Select
                placeholder="Filter by Tenant"
                value={filterTenant}
                onChange={setFilterTenant}
                allowClear
                style={{ width: 200 }}
              >
                <Option value="">All Tenants</Option>
                {tenants.map((tenant) => (
                  <Option key={tenant._id} value={tenant._id}>
                    {tenant.name}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="Filter by Role"
                value={filterRole}
                onChange={setFilterRole}
                allowClear
                style={{ width: 150 }}
              >
                <Option value="">All Roles</Option>
                <Option value="super_admin">Super Admin</Option>
                <Option value="tenant_admin">Tenant Admin</Option>
                <Option value="crm_user">CRM User</Option>
              </Select>
              <Select
                placeholder="Filter by Status"
                value={filterStatus}
                onChange={setFilterStatus}
                allowClear
                style={{ width: 150 }}
              >
                <Option value="">All Status</Option>
                <Option value="true">Active</Option>
                <Option value="false">Inactive</Option>
              </Select>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={showCreateModal}
              >
                Create User
              </Button>
            </Space>
          </div>
        </div>
      </Card>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={totalUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Users"
              value={totalActive}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Admin Users"
              value={totalAdmins}
              prefix={<TeamOutlined />}
              valueStyle={{ color: "#f5222d" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Organizations"
              value={stats?.organizations || 0} // Access directly, provide default
              prefix={<TeamOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Two Column Layout */}
      <Row gutter={16}>
        {/* Left Column - Users List */}
        <Col span={12}>
          <Card title="Users List" style={{ height: "70vh", overflow: "auto" }}>
            {viewMode === "hierarchical" ? (
              <Tree
                showLine={{
                  showLeafIcon: false,
                }}
                switcherIcon={<DownOutlined />}
                expandedKeys={expandedKeys}
                onExpand={setExpandedKeys}
                treeData={convertUsersToTreeData(users)}
                titleRender={renderTreeNode}
                style={{
                  background: "transparent",
                  fontSize: "14px",
                }}
                blockNode
              />
            ) : (
              <Table
                columns={columns}
                dataSource={flattenUsers(users)}
                rowKey="_id"
                loading={loading}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
                onChange={handleTableChange}
                scroll={{ x: 800 }}
                onRow={(record) => ({
                  onClick: () => setSelectedUser(record),
                  style: {
                    cursor: "pointer",
                    backgroundColor:
                      selectedUser && selectedUser._id === record._id
                        ? "#e6f7ff"
                        : "transparent",
                  },
                })}
              />
            )}
          </Card>
        </Col>

        {/* Right Column - User Details */}
        <Col span={12}>
          <Card
            title="User Details"
            style={{ height: "70vh", overflow: "auto" }}
          >
            {selectedUser ? (
              <UserDetailsPanel user={selectedUser} />
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "#999",
                }}
              >
                <UserOutlined
                  style={{ fontSize: "48px", marginBottom: "16px" }}
                />
                <Typography.Text type="secondary">
                  Select a user from the list to view details
                </Typography.Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Create User Modal */}
      <Modal
        title="Create New User"
        open={createModalVisible}
        onCancel={handleCreateCancel}
        footer={null}
        width={600}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateUser}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[
                  { required: true, message: "Please enter first name" },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === "") {
                        return Promise.reject(
                          new Error("First name cannot be empty or just spaces")
                        );
                      }
                      if (!/^[a-zA-Z\s-]*$/.test(value)) {
                        return Promise.reject(
                          new Error(
                            "First name can only contain alphabets, spaces, and hyphens"
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[
                  { required: true, message: "Please enter last name" },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === "") {
                        return Promise.reject(
                          new Error("Last name cannot be empty or just spaces")
                        );
                      }
                      if (!/^[a-zA-Z\s-]*$/.test(value)) {
                        return Promise.reject(
                          new Error(
                            "Last name can only contain alphabets, spaces, and hyphens"
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter valid email" },
              {
                validator: (_, value) =>
                  value && value.trim() !== ""
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Email cannot be empty or just spaces")
                      ),
              },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: "Please enter password" },
              { min: 8, message: "Password must be at least 8 characters" },
              {
                validator: (_, value) =>
                  value && value.trim() !== ""
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Password cannot be empty or just spaces")
                      ),
              },
            ]}
          >
            <Input.Password placeholder="Enter password" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label="Role"
                rules={[{ required: true, message: "Please select role" }]}
              >
                <Select placeholder="Select role">
                  {/* <Option value="crm_user">CRM User</Option> */}
                  <Option value="tenant_admin">Tenant Admin</Option>
                  <Option value="super_admin">Super Admin</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="tenantId"
                label="Organization"
                rules={[
                  { required: true, message: "Please select organization" },
                  {
                    validator: (_, value) =>
                      value && value.trim() !== ""
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              "Organization cannot be empty or just spaces"
                            )
                          ),
                  },
                ]}
              >
                <Select placeholder="Select organization">
                  {organizations.map((org) => (
                    <Option key={org._id} value={org._id}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve(); // Phone is optional
                      }
                      if (!/^\d{10}$/.test(value)) {
                        return Promise.reject(
                          new Error("Phone number must be 10 digits")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="department" label="Department">
                <Input placeholder="Enter department" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="Status"
                initialValue={true}
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select placeholder="Select status">
                  <Option value={true}>Active</Option>
                  <Option value={false}>Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="emailVerified"
                label="Email Verified"
                initialValue={false}
              >
                <Select placeholder="Select verification status">
                  <Option value={true}>Verified</Option>
                  <Option value={false}>Not Verified</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button onClick={handleCreateCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={createLoading}>
                Create User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={editModalVisible}
        onCancel={handleEditCancel}
        footer={null}
        width={600}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[
                  { required: true, message: "Please enter first name" },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === "") {
                        return Promise.reject(
                          new Error("First name cannot be empty or just spaces")
                        );
                      }
                      if (!/^[a-zA-Z\s-]*$/.test(value)) {
                        return Promise.reject(
                          new Error(
                            "First name can only contain alphabets, spaces, and hyphens"
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[
                  { required: true, message: "Please enter last name" },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === "") {
                        return Promise.reject(
                          new Error("Last name cannot be empty or just spaces")
                        );
                      }
                      if (!/^[a-zA-Z\s-]*$/.test(value)) {
                        return Promise.reject(
                          new Error(
                            "Last name can only contain alphabets, spaces, and hyphens"
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter a valid email" },
              {
                validator: (_, value) =>
                  value && value.trim() !== ""
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Email cannot be empty or just spaces")
                      ),
              },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label="Role"
                rules={[{ required: true, message: "Please select role" }]}
              >
                <Select placeholder="Select role">
                  <Option value="tenant_admin">Tenant Admin</Option>
                  <Option value="super_admin">Super Admin</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="tenantId"
                label="Organization"
                rules={[
                  { required: true, message: "Please select organization" },
                  {
                    validator: (_, value) =>
                      value && value.trim() !== ""
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              "Organization cannot be empty or just spaces"
                            )
                          ),
                  },
                ]}
              >
                <Select placeholder="Select organization">
                  {organizations.map((org) => (
                    <Option key={org._id} value={org._id}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve(); // Phone is optional
                      }
                      if (!/^\d{10}$/.test(value)) {
                        return Promise.reject(
                          new Error("Phone number must be 10 digits")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="department" label="Department">
                <Input placeholder="Enter department" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="Status"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select placeholder="Select status">
                  <Option value={true}>Active</Option>
                  <Option value={false}>Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="emailVerified" label="Email Verified">
                <Select placeholder="Select verification status">
                  <Option value={true}>Verified</Option>
                  <Option value={false}>Not Verified</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={handleEditCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Update User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
